"use client";

import { PROVVAKT_AKTIVT_PASS_MS, PROVVAKT_MS } from "@/lib/klient-intervall";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";

// Provläges-vakten — den KLIENT-sidiga halvan av det exklusiva elevläget.
// Servern (proxy.ts, prov-lage-grind.ts) är den faktiska grinden; den här
// komponenten gör låset SYNLIGT för eleven själv. Runda 3 (2026-09-11): den
// tvingande "prov väntar"-popupen (N8) är BORTA — eleven startar provet
// själv från startsidan (MinaProvKort/ProvElevClient), utan att bli
// avbruten någon annanstans på sajten. Kvar:
//
// 1. "aktivt" (eleven har startat) → om eleven av något skäl inte redan är på
//    /prov-elev/[passId] (t.ex. proxyn hann inte, eller en gammal flik),
//    tvinga dit med router.replace — en backstop, inte den primära grinden.
// 2. 5-minuters-varning härledd ur slutTid, en gång per slutTid (ändras
//    slutTid — läraren förlänger provet — nollställs markören så varningen
//    kan visas igen för den nya tiden). Visas ENDAST när eleven redan är
//    inne i provet (/prov-elev/*) — aldrig som en popup någon annanstans.
//
// Adaptiv polling: glest (30 s) tills ett aktivt pass dyker upp, sedan tätt
// (6 s) medan det pågår. Pollar aldrig för lärare/admin/skoladmin, aldrig
// under impersonation (matchar SEB:s och provläges-grindens egna undantag),
// och aldrig på /login (session finns inte där, och sidan renderas ofta
// utloggad).


interface AktivtData {
  passId: string;
  provPassElevId: string;
  titel: string;
  slutTid: string | null;
  aktivDelNamn: string | null;
}

interface AktivtSvar {
  aktivt?: AktivtData;
}

export default function ProvVakt({
  roll,
  impersonerar,
}: {
  roll: string | null;
  impersonerar: boolean;
}) {
  const [femMinVarning, setFemMinVarning] = useState(false);
  const varnadForSlutTid = useRef<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  // usePathname changes identity every navigation — read the LATEST value
  // from a ref inside the poll loop instead of restarting the effect (and
  // thereby the poll timer) on every single client-side navigation.
  const aktivPathnameRef = useRef(pathname);
  aktivPathnameRef.current = pathname;

  const skaPolla = roll === "elev" && !impersonerar && pathname !== "/login";

  useEffect(() => {
    if (!skaPolla) return;

    let cancelled = false;
    let timer: number | null = null;
    let abort: AbortController | null = null;

    function hanteraFemMinVarning(aktivt: AktivtData) {
      if (!aktivPathnameRef.current.startsWith("/prov-elev/")) return;
      if (!aktivt.slutTid) return;
      const kvarMs = new Date(aktivt.slutTid).getTime() - Date.now();
      if (kvarMs > 0 && kvarMs <= 5 * 60 * 1000) {
        if (varnadForSlutTid.current !== aktivt.slutTid) {
          varnadForSlutTid.current = aktivt.slutTid;
          setFemMinVarning(true);
        }
      } else if (varnadForSlutTid.current !== aktivt.slutTid) {
        // Sluttiden ändrades (läraren förlängde provet) innan 5-minuters-
        // fönstret nåddes — nollställ markören så en ny varning kan visas
        // när DEN tiden i sin tur närmar sig.
        varnadForSlutTid.current = null;
      }
    }

    // Always replace the one pending timer, so a tick overtaken by onSynlig cannot leave a
    // second polling chain behind (pre-deploy review 2026-10-21).
    function schemalagg(ms: number) {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(tick, ms);
    }

    async function tick() {
      // A hidden tab neither needs the backstop redirect nor the 5-minute warning, and
      // every student has this poll on every page (prestanda-rattningslatens.md §10b P1b).
      // Becoming visible again polls at once (onSynlig below).
      if (document.hidden) {
        schemalagg(PROVVAKT_MS);
        return;
      }
      abort = new AbortController();
      let nastaDelayMs = PROVVAKT_MS;
      try {
        const res = await fetch("/api/prov-elev/aktivt", {
          cache: "no-store",
          signal: abort.signal,
        });
        if (cancelled) return;
        if (!res.ok) return;

        const data = (await res.json()) as AktivtSvar;

        if (data.aktivt) {
          nastaDelayMs = PROVVAKT_AKTIVT_PASS_MS;
          // Backstop: eleven ska aldrig stå kvar utanför provsidan när
          // passet är aktivt. Proxyn/prov-lage-grind är den faktiska
          // grinden — det här täcker en klient som redan renderade sidan
          // innan grinden hann svara (t.ex. en gammal flik som vaknar).
          if (!aktivPathnameRef.current.startsWith(`/prov-elev/${data.aktivt.passId}`)) {
            router.replace(`/prov-elev/${data.aktivt.passId}`);
          }
          hanteraFemMinVarning(data.aktivt);
        }
      } catch {
        // Tyst — abort vid unmount, eller nätfel. Nästa tick försöker igen.
      } finally {
        if (!cancelled) schemalagg(nastaDelayMs);
      }
    }

    function onSynlig() {
      if (document.hidden || cancelled) return;
      if (timer !== null) window.clearTimeout(timer);
      if (abort) abort.abort();
      void tick();
    }
    document.addEventListener("visibilitychange", onSynlig);

    tick();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onSynlig);
      if (abort) abort.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [skaPolla, router]);

  if (!skaPolla || typeof document === "undefined") return null;

  if (femMinVarning) {
    return createPortal(
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="provvakt-femmin-rubrik"
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      >
        <div className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-xl">
          <h2
            id="provvakt-femmin-rubrik"
            className="mb-2 text-base font-semibold text-[var(--color-text)]"
          >
            5 minuter kvar av provtiden
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-[var(--color-text)]">
            Provet lämnas in automatiskt när tiden går ut.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setFemMinVarning(false)}
              title="Stäng notisen om att provtiden snart går ut"
              className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return null;
}
